import { Box, CircularProgress, TableCell, TableRow } from '@mui/material';

type TableLoaderProps = {
  /** Number of columns to span so the row occupies the whole table. */
  colSpan: number;
  /** Approximate number of data rows to reserve height for (default page size). */
  rows?: number;
  /** Per-row height used to size the placeholder. */
  rowHeight?: number;
};

/**
 * A single full-width table row showing a centered spinner, used as the
 * `<TableBody>` content while data is loading.
 *
 * Two details matter:
 *  - The spinner is `position: sticky; left: 0` and sized to the viewport
 *    (`100vw`, capped at the cell width) so it stays visible even on wide tables
 *    with many columns — otherwise it centers across the full (off-screen) table
 *    width and you never see it.
 *  - The placeholder is ~`rows` tall (the default page size, 10) so when the
 *    data arrives the table height barely changes and the screen doesn't jump.
 *
 * Usage:
 *   <TableBody>
 *     {loading ? <TableLoader colSpan={COLUMN_COUNT} /> : rows.map(...)}
 *   </TableBody>
 */
const TableLoader = ({ colSpan, rows = 10, rowHeight = 44 }: TableLoaderProps) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ border: 0, p: 0 }}>
      <Box
        sx={{
          position: 'sticky',
          left: 0,
          width: '100vw',
          maxWidth: '100%',
          height: rows * rowHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    </TableCell>
  </TableRow>
);

export default TableLoader;
